import React, { useState, useEffect } from 'react';
import { FileText, Download, Edit3, CheckCircle, Loader2, Sparkles, Eye, Save, BarChart3, TrendingUp, Activity, ArrowLeft, Home, AlertTriangle, Zap, Pencil } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { FinancialValue } from '../../components/shared/FinancialValue';

// Claim Breakdown Chart Component
const ClaimBreakdownChart = ({ step1, step2, total }: { step1: number; step2: number; total: number }) => {
  if (!total || total <= 0) return null;
  
  const step1Pct = (step1 / total) * 100;
  const step2Pct = (step2 / total) * 100;
  
  return (
    <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-base)', border: '1px solid var(--color-border)' }}>
      <h4 className="cs-h3 mb-4 flex items-center gap-2">
        <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
        Claim Amount Breakdown
      </h4>

      <div className="space-y-4">
        <div>
          <div className="flex justify-between cs-body mb-1">
            <span>Step 1: Variable Profit Loss</span>
            <span className="cs-figure" style={{ fontWeight: 600 }}><FinancialValue value={step1} cents /> ({step1Pct.toFixed(0)}%)</span>
          </div>
          <div className="h-6 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
            <div
              className="h-full rounded-full flex items-center justify-end pr-2"
              style={{ width: `${step1Pct}%`, background: 'var(--color-brand)' }}
            >
              {step1Pct > 15 && <span className="cs-figure text-xs" style={{ color: 'var(--color-on-brand)', fontWeight: 700 }}><FinancialValue value={step1} cents /></span>}
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between cs-body mb-1">
            <span>Step 2: Incremental Loss</span>
            <span className="cs-figure" style={{ fontWeight: 600 }}><FinancialValue value={step2} cents /> ({step2Pct.toFixed(0)}%)</span>
          </div>
          <div className="h-6 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
            <div
              className="h-full rounded-full flex items-center justify-end pr-2"
              style={{ width: `${step2Pct}%`, background: 'var(--color-brand-hover)' }}
            >
              {step2Pct > 15 && <span className="cs-figure text-xs" style={{ color: 'var(--color-on-brand)', fontWeight: 700 }}><FinancialValue value={step2} cents /></span>}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 text-center" style={{ borderTop: '1px solid var(--color-border)' }}>
        <span className="cs-figure" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-verified)' }}>Total Claim: <FinancialValue value={total} cents /></span>
      </div>
    </div>
  );
};

// Sensitivity Analysis Chart Component
const SensitivityChart = ({ data }: { data: any }) => {
  if (!data || !data.results) return null;
  
  const results = data.results;
  const base = results.base_claim || 0;
  const range = results.range || {};
  const minVal = range.min || 0;
  const maxVal = range.max || 0;
  const spread = range.spread || 0;
  const param = (data.parameter || 'parameter').replace('_', ' ');
  const spreadPct = base > 0 ? (spread / base) * 100 : 0;
  
  const getSensitivityLabel = () => {
    if (spreadPct < 15) return { label: 'Low Sensitivity', color: 'var(--color-verified)', bg: 'transparent' };
    if (spreadPct < 30) return { label: 'Moderate Sensitivity', color: 'var(--color-needs-human-text)', bg: 'var(--color-needs-human-tint)' };
    return { label: 'High Sensitivity', color: 'var(--color-needs-human-text)', bg: 'var(--color-needs-human-tint)' };
  };

  const sensitivity = getSensitivityLabel();

  return (
    <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      <h4 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
        <TrendingUp className="w-5 h-5" />
        Sensitivity Analysis: {param.charAt(0).toUpperCase() + param.slice(1)}
      </h4>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)' }}>
          <div className="cs-meta">Min (-20%)</div>
          <div className="cs-figure" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-needs-human-text)' }}><FinancialValue value={minVal} cents /></div>
        </div>
        <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-verified)' }}>
          <div className="cs-meta" style={{ color: 'var(--color-on-brand)' }}>Base Claim</div>
          <div className="cs-figure" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-on-brand)' }}><FinancialValue value={base} cents /></div>
        </div>
        <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)' }}>
          <div className="cs-meta">Max (+20%)</div>
          <div className="cs-figure" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-needs-human-text)' }}><FinancialValue value={maxVal} cents /></div>
        </div>
      </div>

      <div className="h-4 rounded-full mb-4 relative" style={{ background: 'var(--color-bg-elevated)' }}>
        <div className="absolute left-1/2 top-0 w-1 h-6 rounded transform -translate-x-1/2 -translate-y-1" style={{ background: 'var(--color-verified)' }}></div>
      </div>

      <div className="text-center">
        <span className="cs-meta">Spread: <FinancialValue value={spread} /> ({spreadPct.toFixed(1)}% of base) — </span>
        <span className="cs-chip px-2 py-1 rounded" style={{ color: sensitivity.color, background: sensitivity.bg }}>{sensitivity.label}</span>
      </div>
    </div>
  );
};

// Monte Carlo Chart Component
const MonteCarloChart = ({ data }: { data: any }) => {
  if (!data || !data.results) return null;
  
  const outerResults = data.results || {};
  const results = outerResults.results || outerResults;
  const iterations = data.iterations || outerResults.iterations || 1000;
  const mean = results.mean || 0;
  const median = results.median || 0;
  const stdDev = results.std_dev || 0;
  const ciLow = results.ci_95_low || 0;
  const ciHigh = results.ci_95_high || 0;
  
  if (ciHigh <= 0) return null;
  
  return (
    <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
      <h4 className="cs-h3 mb-4 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
        <Activity className="w-5 h-5" />
        Monte Carlo Simulation ({iterations.toLocaleString()} iterations)
      </h4>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)' }}>
          <div className="cs-meta">Mean</div>
          <div className="cs-figure" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-needs-human-text)' }}><FinancialValue value={mean} /></div>
        </div>
        <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)' }}>
          <div className="cs-meta">Median</div>
          <div className="cs-figure" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-needs-human-text)' }}><FinancialValue value={median} /></div>
        </div>
        <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-bg-elevated)' }}>
          <div className="cs-meta">Std Dev</div>
          <div className="cs-figure" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-needs-human-text)' }}><FinancialValue value={stdDev} /></div>
        </div>
      </div>

      <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-elevated)' }}>
        <div className="text-center cs-label mb-3" style={{ color: 'var(--color-needs-human-text)' }}>95% Confidence Interval</div>
        <div className="flex items-center justify-between">
          <div className="text-center">
            <div className="cs-meta">Low Bound</div>
            <div className="cs-figure" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-needs-human-text)' }}><FinancialValue value={ciLow} cents /></div>
          </div>
          <div className="flex-1 mx-4 h-6 rounded-full relative" style={{ background: 'var(--color-needs-human-tint)' }}>
            <div className="absolute left-1/2 top-0 w-1 h-8 rounded transform -translate-x-1/2 -translate-y-1" style={{ background: 'var(--color-brand)' }}></div>
          </div>
          <div className="text-center">
            <div className="cs-meta">High Bound</div>
            <div className="cs-figure" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-needs-human-text)' }}><FinancialValue value={ciHigh} cents /></div>
          </div>
        </div>
      </div>

      <div className="mt-4 text-center cs-body p-3 rounded-lg" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
        <strong>Interpretation:</strong> 95% probability the true claim value lies within <FinancialValue value={ciLow} cents /> to <FinancialValue value={ciHigh} cents />
      </div>
    </div>
  );
};

/**
 * AI Report Narrator - Frontend Editor
 * Premium Feature: $200/report
 * 
 * Allows users to:
 * - Generate AI-powered 30-40 page comprehensive legal narrative
 * - View existing narratives without regeneration
 * - Edit sections as needed
 * - Preview in real-time
 * - Save to database
 * - Export to PDF anytime
 */

interface NarrativeEditorProps {
  claimId: string;
  claimData: any;
  calculationResults: any;
  eventType: string;
  existingNarrative?: any;  // Load from report if exists
  // `isAccountantView` prop removed in Phase 00.3 — detect via route.
}

export default function NarrativeEditor({
  claimId,
  claimData,
  calculationResults,
  eventType,
  existingNarrative,
}: NarrativeEditorProps) {
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [narrative, setNarrative] = useState(existingNarrative || null);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionContent, setSectionContent] = useState('');
  const [refining, setRefining] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editorMessage, setEditorMessage] = useState<string | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Route-based detection replaces the old `isAccountantView` prop (Phase 00.3).
  // The external accountant JWT portal lives under `/accountant/*`, so the
  // path alone is an authoritative signal for portal vs Clerk-gated flows.
  const isAccountantView = location.pathname.startsWith('/accountant/');

  // Determine back navigation path
  const getBackPath = () => {
    if (isAccountantView) {
      return `/accountant/reports/${claimId}`;
    }
    return `/report/${claimId}`;
  };

  const getDashboardPath = () => {
    if (isAccountantView) {
      // Phase 08: external witness accountants land on /accountant/home
      // (the pending-work dashboard). /accountant/dashboard is Clerk-gated
      // (in-house staff) and would bounce JWT users to login.
      return '/accountant/home';
    }
    return '/dashboard';
  };

  // Update narrative when existingNarrative prop changes (e.g., after fetch)
  useEffect(() => {
    if (existingNarrative && !narrative) {
      setNarrative(existingNarrative);
      console.log('Loaded existing narrative from database');
    }
  }, [existingNarrative]);

  // Section definitions
  const sections = [
    { id: 'executive_summary', name: 'Executive Summary', pages: 1 },
    { id: 'causation_analysis', name: 'Causation Analysis', pages: 2 },
    { id: 'methodology', name: 'Methodology Overview', pages: 2 },
    { id: 'benchmark_analysis', name: 'Benchmark Period Analysis', pages: 2 },
    { id: 'detailed_calculations', name: 'Detailed Calculations', pages: 3 },
    { id: 'assumptions', name: 'Assumptions & Limitations', pages: 2 },
    { id: 'attestation', name: 'Professional Attestation', pages: 1 },
    { id: 'conclusion', name: 'Conclusion & Recommendation', pages: 1 }
  ];

  // Generate narrative
  const handleGenerateNarrative = async () => {
    setGenerating(true);
    try {
      const token = await getToken();
      const result = await api.post('/api/v1/narrator/generate-narrative', {
        claim_id: claimId,
        claim_data: claimData,
        calculation_results: calculationResults,
        event_type: eventType
      }, { headers: { Authorization: `Bearer ${token}` } });

      setNarrative(result.narrative);
      
      setEditorMessage(`Generated ${result.narrative.estimated_pages}-page report (${result.narrative.word_count} words). Review each section below.`);
    } catch (error: any) {
      console.error('Generation error:', error);
      setEditorError(error.message || 'Failed to generate narrative. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Edit section
  const handleEditSection = (sectionId: string) => {
    // If sections are empty (MVP parsing), show full narrative for editing
    const sectionText = narrative?.narrative_sections?.[sectionId];
    const fullText = narrative?.narrative_sections?.full_narrative || narrative?.full_narrative || '';
    
    const content = sectionText && sectionText.trim().length > 0 ? sectionText : fullText;
    setSectionContent(content);
    setEditingSection(sectionId);
  };

  // Refine section with AI
  const handleRefineSection = async (feedback: string) => {
    if (!feedback.trim()) {
      setEditorError('Please provide feedback for AI refinement');
      return;
    }

    setRefining(true);
    try {
      const token = await getToken();
      const result = await api.post('/api/v1/narrator/refine-section', {
        claim_id: claimId,
        section_name: editingSection,
        current_text: sectionContent,
        user_feedback: feedback
      }, { headers: { Authorization: `Bearer ${token}` } });

      setSectionContent(result.refined_text);
      
      // Update in narrative
      setNarrative(prev => ({
        ...prev,
        narrative_sections: {
          ...prev.narrative_sections,
          [editingSection]: result.refined_text
        }
      }));

      setEditorMessage('Section refined by AI');
    } catch (error) {
      console.error('Refinement error:', error);
      setEditorError('Failed to refine section');
    } finally {
      setRefining(false);
    }
  };

  // Save section locally and mark as having unsaved changes
  const handleSaveSection = () => {
    setNarrative(prev => ({
      ...prev,
      narrative_sections: {
        ...prev.narrative_sections,
        [editingSection]: sectionContent
      }
    }));
    setEditingSection(null);
    setHasUnsavedChanges(true);
    setEditorMessage('Section saved locally. Click "Save All Changes" to persist to database.');
  };

  // Save all changes to database
  const handleSaveToDatabase = async () => {
    if (!narrative) return;
    
    setSaving(true);
    try {
      const token = await getToken();
      await api.post('/api/v1/narrator/save-narrative', {
        claim_id: claimId,
        narrative_sections: narrative.narrative_sections,
        word_count: narrative.word_count,
        estimated_pages: narrative.estimated_pages
      }, { headers: { Authorization: `Bearer ${token}` } });

      setHasUnsavedChanges(false);
      setEditorMessage('All changes saved to database successfully!');
    } catch (error: any) {
      console.error('Save error:', error);
      setEditorError(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Export to PDF
  const handleExportPDF = async () => {
    // First save any pending changes
    if (hasUnsavedChanges) {
      await handleSaveToDatabase();
    }
    
    setGenerating(true);
    try {
      const token = await getToken();
      const result = await api.post('/api/v1/narrator/generate-narrative-pdf', {
        claim_id: claimId,
        claim_data: claimData,
        calculation_results: {
          ...calculationResults,
          narrative_sections: narrative.narrative_sections
        },
        event_type: eventType
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Download the PDF
      window.open(result.pdf_url, '_blank');
      
      setEditorMessage('14-page PDF generated! Opening download...');
      
      // Optionally navigate back to report detail
      // navigate(`/report/${claimId}`);
      
    } catch (error) {
      console.error('PDF export error:', error);
      setEditorError('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ background: 'var(--color-bg-base)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Navigation Bar */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(getBackPath())}
              className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Report
            </button>
            <span style={{ color: 'var(--color-text-ghost)' }}>|</span>
            <button
              onClick={() => navigate(getDashboardPath())}
              className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <Home className="w-5 h-5" />
              Dashboard
            </button>
          </div>
          <div className="cs-meta">
            Claim: {claimData?.claimant_name || claimData?.client_name || 'Unknown'}
          </div>
        </div>

        {/* Header */}
        <div className="rounded-xl shadow-lg p-8 mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-8 h-8" style={{ color: 'var(--color-brand)' }} />
                <h1 className="cs-display">AI Report Narrator</h1>
                <span className="cs-chip px-3 py-1 rounded-full" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                  Premium $150
                </span>
              </div>
              <p className="cs-body">Generate court-ready 14-page legal narrative in seconds</p>
            </div>
            <div className="flex gap-3">
              {!narrative ? (
                <button
                  onClick={handleGenerateNarrative}
                  disabled={generating}
                  className="cs-body px-8 py-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 700, fontSize: '1.125rem' }}
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate AI Report
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to regenerate the narrative? This will replace the existing content.')) {
                      handleGenerateNarrative();
                    }
                  }}
                  disabled={generating}
                  className="cs-body px-6 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 700 }}
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Regenerate Report
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Generation in progress */}
        {generating && (
          <div className="rounded-xl shadow-lg p-12 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
            <h2 className="cs-h2 mb-2">AI is writing your report...</h2>
            <p className="cs-body mb-4">This takes 30-60 seconds</p>
            <div className="max-w-md mx-auto rounded p-4" style={{ background: 'var(--color-bg-elevated)' }}>
              <p className="cs-body">
                Claude 3.5 Sonnet is analyzing your claim data, BI calculations, and generating a
                professional legal narrative with proper citations, methodology explanations, and attestation language.
              </p>
            </div>
          </div>
        )}

        {/* Narrative generated - Section editor */}
        {narrative && !editingSection && (
          <div className="space-y-4">
            {/* Unsaved changes warning */}
            {hasUnsavedChanges && (
              <div className="rounded-xl p-4 flex items-center justify-between" style={{ background: 'var(--color-needs-human-tint)', border: '2px solid var(--color-needs-human-tint-bd)' }}>
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: 'var(--color-needs-human-text)' }} />
                  <div>
                    <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>You have unsaved changes</p>
                    <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>Click "Save All Changes" to persist your edits to the database</p>
                  </div>
                </div>
                <button
                  onClick={handleSaveToDatabase}
                  disabled={saving}
                  className="cs-body flex items-center gap-2 px-6 py-3 rounded-lg transition disabled:opacity-50"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 700 }}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save All Changes
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Warning for empty/broken narratives */}
            {narrative && (!narrative.word_count || narrative.word_count === 0) && (
              <div className="rounded-xl p-4 mb-4 flex items-center justify-between" style={{ background: 'var(--color-needs-human-tint)', border: '2px solid var(--color-needs-human-tint-bd)' }}>
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: 'var(--color-needs-human-text)' }} />
                  <div>
                    <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>Empty or Failed Narrative</p>
                    <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>This narrative appears to be empty or failed to generate. Click "Regenerate Report" above to create a new one.</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="rounded-xl shadow-lg p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="cs-h2">Your AI-Generated Report</h2>
                  <p className="cs-body">
                    {narrative.estimated_pages || 0} pages • {narrative.word_count || 0} words • Review & edit each section
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition"
                    style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                  >
                    <Eye className="w-4 h-4" />
                    {showPreview ? 'Hide' : 'Show'} Preview
                  </button>
                  {hasUnsavedChanges && (
                    <button
                      onClick={handleSaveToDatabase}
                      disabled={saving}
                      className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition disabled:opacity-50"
                      style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  )}
                  <button
                    onClick={handleExportPDF}
                    disabled={generating}
                    className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition disabled:opacity-50"
                    style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                  >
                    <Download className="w-4 h-4" />
                    Export PDF
                  </button>
                </div>
              </div>

              {/* Section cards */}
              <div className="grid grid-cols-1 gap-4">
                {/* Single comprehensive report card */}
                <div
                  className="rounded-lg p-6 transition cursor-pointer"
                  style={{ border: '2px solid var(--color-border)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; }}
                  onClick={() => handleEditSection('full_narrative')}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
                      <h3 className="cs-h3">Court-Ready Expert Report</h3>
                    </div>
                    <span className="cs-meta">
                      {narrative?.estimated_pages || 5} pages • {narrative?.word_count || 0} words
                    </span>
                  </div>
                  <div className="mb-3">
                    <p className="cs-body mb-2" style={{ fontWeight: 500 }}>Report Includes:</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 cs-meta">
                      <div className="flex items-center gap-1">
                        <span style={{ color: 'var(--color-verified)' }}>✓</span> Executive Summary
                      </div>
                      <div className="flex items-center gap-1">
                        <span style={{ color: 'var(--color-verified)' }}>✓</span> Analysis & Findings
                      </div>
                      <div className="flex items-center gap-1">
                        <span style={{ color: 'var(--color-verified)' }}>✓</span> Financial Calculations
                      </div>
                      <div className="flex items-center gap-1">
                        <span style={{ color: 'var(--color-verified)' }}>✓</span> Professional Opinion
                      </div>
                      <div className="flex items-center gap-1">
                        <span style={{ color: 'var(--color-verified)' }}>✓</span> Conclusion
                      </div>
                      <div className="flex items-center gap-1">
                        <span style={{ color: 'var(--color-verified)' }}>✓</span> CPA Attestation
                      </div>
                    </div>
                  </div>
                  <p className="cs-body mb-3 line-clamp-3">
                    {narrative.narrative_sections?.full_narrative?.substring(0, 200) ||
                     'Click to view and edit the complete expert report'}...
                  </p>
                  <button className="cs-body flex items-center gap-1 hover:underline" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>
                    <Edit3 className="w-4 h-4" />
                    Review & Edit Report
                  </button>
                </div>
              </div>
            </div>

            {/* Visual Charts Section */}
            <div className="rounded-xl shadow-lg p-8" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <h3 className="cs-h2 mb-6 flex items-center gap-2">
                <BarChart3 className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
                Visual Analysis Charts
                <span className="cs-meta ml-2">(Included in PDF export)</span>
              </h3>
              <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Claim Breakdown Chart */}
                <ClaimBreakdownChart 
                  step1={calculationResults?.step1_variable_profit_loss || 0}
                  step2={calculationResults?.step2_incremental_loss || 0}
                  total={calculationResults?.final_claim_amount || 0}
                />
                
                {/* Sensitivity Analysis Chart */}
                {calculationResults?.sensitivity_analysis && (
                  <SensitivityChart data={calculationResults.sensitivity_analysis} />
                )}
                
                {/* Monte Carlo Chart */}
                {calculationResults?.monte_carlo_analysis && (
                  <MonteCarloChart data={calculationResults.monte_carlo_analysis} />
                )}
                
                {/* No analysis message */}
                {!calculationResults?.sensitivity_analysis && !calculationResults?.monte_carlo_analysis && (
                  <div className="col-span-full rounded-xl p-6 text-center" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                    <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
                      <strong>Statistical analyses not yet run.</strong> Go to the Report Detail page to run Sensitivity Analysis and Monte Carlo Simulation for enhanced charts.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Full Report Preview - Complete Narrative */}
            {showPreview && (
              <div className="rounded-xl shadow-lg p-8" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <h3 className="cs-h2 mb-6 flex items-center gap-2">
                  <Eye className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
                  Full Report Preview
                </h3>
                <div className="space-y-8">
                  {/* Display the complete unified narrative */}
                  {narrative.narrative_sections?.full_narrative ? (
                    <div className="pl-6" style={{ borderLeft: '4px solid var(--color-brand)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="cs-h3">
                          Court-Ready Expert Report
                        </h4>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSection('full_narrative');
                          }}
                          className="cs-meta flex items-center gap-1"
                          style={{ color: 'var(--color-needs-human-text)' }}
                        >
                          <Edit3 className="w-4 h-4" />
                          Edit Section
                        </button>
                      </div>
                      <div className="p-6 rounded-lg" style={{ background: 'var(--color-bg-base)' }}>
                        <pre className="whitespace-pre-wrap font-serif leading-relaxed" style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                          {narrative.narrative_sections.full_narrative}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <p className="cs-body text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
                      No narrative generated yet. Click "Generate Narrative" to create your court-ready expert report.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Section editor modal */}
        {editingSection && (
          <div className="rounded-xl shadow-2xl p-8" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="cs-h2">
                Editing: {sections.find(s => s.id === editingSection)?.name}
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={() => setEditingSection(null)}
                  className="cs-body px-4 py-2"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSection}
                  className="cs-body flex items-center gap-2 px-6 py-2 rounded-lg transition"
                  style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                >
                  <Save className="w-4 h-4" />
                  Save Section
                </button>
              </div>
            </div>

            {/* Text editor */}
            <textarea
              value={sectionContent}
              onChange={(e) => setSectionContent(e.target.value)}
              className="w-full h-96 p-4 rounded-lg font-serif focus:outline-none"
              style={{ border: '2px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; }}
              placeholder="Edit your content here..."
            />

            {/* AI refinement */}
            <div className="mt-4 rounded-lg p-4" style={{ background: 'var(--color-needs-human-tint)', border: '2px solid var(--color-needs-human-tint-bd)' }}>
              <h3 className="cs-h3 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                AI Refinement Assistant
              </h3>
              <p className="cs-body mb-3">
                Tell AI how to improve this section (e.g., "Make it more formal", "Add more detail about causation", "Simplify language")
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="How should AI refine this section?"
                  className="cs-body flex-1 px-4 py-2 rounded-lg"
                  style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.currentTarget.value) {
                      handleRefineSection(e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                    handleRefineSection(input.value);
                    input.value = '';
                  }}
                  disabled={refining}
                  className="cs-body px-6 py-2 rounded-lg disabled:opacity-50 transition"
                  style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {refining ? 'Refining...' : 'Refine'}
                </button>
              </div>
            </div>

            {/* Character count */}
            <div className="mt-4 text-right cs-meta">
              {sectionContent.length} characters • {sectionContent.split(/\s+/).length} words
            </div>
          </div>
        )}

        {/* Help text when no narrative */}
        {!narrative && !generating && (
          <div className="rounded-xl shadow-lg p-12 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-dim)' }} />
            <h2 className="cs-h2 mb-2">Ready to generate your report?</h2>
            <p className="cs-body mb-6 max-w-2xl mx-auto">
              Click "Generate AI Report" above to create a comprehensive 14-page legal narrative
              based on your claim data and BI calculations. The AI will write all sections in
              professional court-ready language.
            </p>
            <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
              <div className="p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                <h3 className="cs-h3 mb-2 flex items-center gap-1.5"><Zap className="w-4 h-4" /> Fast</h3>
                <p className="cs-meta">Generated in 30-60 seconds instead of 3-4 hours manual writing</p>
              </div>
              <div className="p-4 rounded-lg" style={{ background: 'transparent' }}>
                <h3 className="cs-h3 mb-2">Court-Ready</h3>
                <p className="cs-meta">Professional legal tone, proper citations, methodology explained</p>
              </div>
              <div className="p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                <h3 className="cs-h3 mb-2 flex items-center gap-1.5"><Pencil className="w-4 h-4" /> Editable</h3>
                <p className="cs-meta">Review, edit, and refine each section with AI assistance</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
